import { useEffect } from 'react'
import { renderMarkdown } from '../lib/markdown.ts'
import { dropLeadingBlockquote } from '../lib/doc-structure.ts'
import { downloadDoc, copyText, renderPrintBlanks } from '../lib/export.ts'
import { useBrandKit } from '../brand/BrandKitContext.tsx'
import type { Sidebar } from '../lib/api.ts'

/** The `.sheet` — signature element. Renders the document as it streams:
 * meta (sidebar + disclaimer) arrives first, then the body fills in. */
export function DocumentView({
  title,
  meta,
  content,
  disclaimer,
  sidebar,
  streaming,
  isDraft = false,
}: {
  title: string
  meta: string
  content: string
  disclaimer: string
  sidebar: Sidebar
  streaming: boolean
  isDraft?: boolean
}) {
  const { brand } = useBrandKit()
  // Print blanks ([[Full Name]] etc.) must render as a labelled fillable rule
  // on screen too, not just in the exported .doc — a raw [[bracket]] reads as
  // a templating bug on a form someone is about to read and sign.
  const renderedHtml = renderPrintBlanks(renderMarkdown(content))
  // The draft notice is the first block of the markdown, so it survives copy and
  // download: a document that leaves this app carries its own warning or it
  // carries none at all. On screen the page already opens with DraftBanner, and
  // a second copy set as a blockquote above the title is where it looked worst.
  // Removed structurally rather than by matching the text, so there is no fourth
  // hardcoded copy of the wording to drift. Matches RenderedDoc.
  const screenHtml = isDraft ? dropLeadingBlockquote(renderedHtml) : renderedHtml

  // Follow the stream: keep the newest text in view as it grows — but only while
  // the reader is already near the bottom, so scrolling up to re-read wins.
  useEffect(() => {
    if (!streaming) return
    const el = document.documentElement
    const distanceFromBottom = el.scrollHeight - window.scrollY - window.innerHeight
    if (distanceFromBottom < 160) window.scrollTo({ top: el.scrollHeight })
  }, [screenHtml, streaming])

  return (
    <section className="panel sheet" aria-live="polite" aria-busy={streaming}>
      <div className="sheet__meta">
        <span>{streaming ? 'Generating…' : meta}</span>
      </div>

      <div className="sheet__body">
        {sidebar && (
          <div className="clock">
            <p className="clock__title">{sidebar.title}</p>
            {sidebar.items.map((item) => (
              <div key={item.label} className="clock__item">
                <span>{item.label}</span>
                <strong>{item.due}</strong>
              </div>
            ))}
          </div>
        )}

        {streaming && !content ? (
          <span className="dots" aria-hidden="true"><i></i><i></i><i></i></span>
        ) : (
          <article
            className={streaming ? 'is-streaming' : undefined}
            dangerouslySetInnerHTML={{ __html: screenHtml }}
          />
        )}

        {/* Server-enforced: always rendered, unconditionally — never gated on the
            string being non-empty. The guarantee must live in this markup, not
            in "the server always sends one today"; do not reinstate a guard. */}
        <p className="disclaimer">{disclaimer}</p>
      </div>

      <div className="sheet__actions">
        <button className="btn btn--soft" disabled={streaming} onClick={() => copyText(content)}>
          Copy
        </button>
        <button
          className="btn btn--soft"
          disabled={streaming}
          onClick={() => downloadDoc(title, brand, renderedHtml)}
        >
          Download .doc
        </button>
      </div>
    </section>
  )
}
